/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled
import { css, jsx } from '@emotion/react';

import { Checkbox } from '@atlaskit/checkbox/checkbox';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, xcss } from '@atlaskit/primitives';
import Radio from '@atlaskit/radio/radio';
import { token } from '@atlaskit/tokens';

import type { ListObject, SelectListProps } from '../types';
import { ContentDropdown } from './content-dropdown';
import { RecommendedBadge } from './recommended-badge';
import {
	CardContainer,
	ColumnContainer,
	ColumnLeftContainer,
	ColumnRightContainer,
	ColumnTopContainer,
	ColumnTopLeftContainer,
	ColumnTopRightContainer,
	Description,
	RowIcon,
	RowImage,
	RowMidContainer,
	RowRadioContainer,
	RowTopContainer,
	Title,
	TitleAndDescriptionContainer,
} from './styled';

const styles = xcss({ marginLeft: 'space.200' });
type CardProps = ListObject & {
	direction: SelectListProps['direction'];
	handleChange(
		event: React.MouseEvent<HTMLElement, MouseEvent> | React.ChangeEvent<HTMLInputElement>,
		value: string,
	): void;
	isMulti?: boolean;
	radioGroupName?: string;
	selected?: string;
	selectedMulti?: string[];
	testId: string;
};

const contentBaseStyles = css({
	font: token('font.body'),
	color: token('color.text.subtlest'),
	marginLeft: token('space.500'),
	marginTop: token('space.200'),
});

const columnContentExpandedStyles = css({
	marginTop: token('space.400'),
});

const rowContentStyles = css({
	margin: `${token('space.100')} 0 0 36px`,
});

export const SelectCard = (props: CardProps): React.JSX.Element => {
	const [isContentExpanded, setIsContentExpanded] = useState(false);

	const {
		icon,
		testId,
		id,
		handleChange,
		selected,
		selectedMulti,
		isMulti,
		title,
		description,
		content,
		contentType = 'default',
		contentExpandableLabel,
		direction = 'column',
		image,
		isRecommended = false,
		radioGroupName,
		isDisabled,
		ariaLabel,
	} = props;

	const isSelected = isMulti ? (selectedMulti?.includes(id) ?? false) : selected === id;

	const handleDropdownClicked = (): void => {
		setIsContentExpanded(!isContentExpanded);
	};

	const control = isMulti ? (
		<Checkbox
			value={`${id}`}
			testId={`${testId}-checkbox`}
			isChecked={isSelected}
			onChange={(e) => {
				handleChange(e, id);
			}}
			onClick={(e) => {
				e.stopPropagation();
			}}
			isDisabled={isDisabled}
			aria-label={ariaLabel || id}
		/>
	) : (
		<Radio
			key={id}
			value={`${id}`}
			name={radioGroupName ? radioGroupName : 'radio-default'}
			testId={`${testId}-radio-default`}
			isChecked={isSelected}
			tabIndex={0}
			onChange={(e) => {
				handleChange(e, id);
			}}
			onClick={(e) => {
				e.stopPropagation();
			}}
			isDisabled={isDisabled}
			ariaLabel={ariaLabel || id}
		/>
	);

	const handleCardContainerClick = (
		e: React.MouseEvent<HTMLElement, MouseEvent>,
		id: string,
	): void => {
		if (isDisabled) {
			e.stopPropagation();
			return;
		}
		handleChange(e, id);
	};

	const columnVariant = (
		<ColumnContainer>
			<ColumnLeftContainer>
				<ColumnTopContainer>
					<ColumnTopLeftContainer>
						{control}
						{icon && <Flex xcss={styles}>{icon}</Flex>}

						<TitleAndDescriptionContainer>
							<Title data-testid={`${testId}-title`}>{title}</Title>
							{description && (
								<Description data-testid={`${testId}-description`}>{description}</Description>
							)}
						</TitleAndDescriptionContainer>
					</ColumnTopLeftContainer>
					<ColumnTopRightContainer>
						{contentType === 'expandable' && (
							<ContentDropdown
								testId={testId}
								isExpanded={isContentExpanded}
								onClick={handleDropdownClicked}
								contentExpandableLabel={contentExpandableLabel}
							/>
						)}
					</ColumnTopRightContainer>
				</ColumnTopContainer>
				<Fragment>
					{contentType === 'expandable' && isContentExpanded && content && (
						<div css={[contentBaseStyles, columnContentExpandedStyles]}>{content}</div>
					)}
					{contentType === 'default' && content && <div css={[contentBaseStyles]}>{content}</div>}
				</Fragment>
			</ColumnLeftContainer>
			{image && <ColumnRightContainer>{image}</ColumnRightContainer>}
		</ColumnContainer>
	);

	const rowVariant = (
		<Fragment>
			{image && (
				<RowTopContainer>
					<RowImage>{image}</RowImage>
				</RowTopContainer>
			)}
			<RowMidContainer>
				<RowRadioContainer>{control}</RowRadioContainer>
				{icon && <RowIcon>{icon}</RowIcon>}
				<Title>{title}</Title>
			</RowMidContainer>
			{content && <div css={[contentBaseStyles, rowContentStyles]}>{content}</div>}
		</Fragment>
	);

	return (
		<CardContainer
			data-testid={testId}
			key={id}
			isSelected={isSelected}
			onClick={(e) => handleCardContainerClick(e, id)}
			direction={direction}
			isDisabled={isDisabled}
		>
			{isRecommended && (
				<RecommendedBadge testId={`${testId}-recommended-badge`} isSelected={isSelected} />
			)}
			{direction === 'column' || direction === 'grid' ? columnVariant : rowVariant}
		</CardContainer>
	);
};
